/**
 * sanitizeHTML.ts — allow-list sanitizer for HTML this application did not author.
 *
 * Product descriptions and release notes come from the licensing backend as HTML.
 * They are written by administrators there, not by arbitrary users, so this is not
 * a hostile-input surface in normal operation — but the output is rendered into an
 * authenticated Abstract ID page, so an injection on that backend would otherwise
 * reach every one of our users' sessions. Closing that is cheap; leaving it open
 * turns a compromise of one admin UI into a compromise of all of them.
 *
 * It parses with the browser's own HTML parser and walks the resulting tree rather
 * than matching patterns in a string. A regex sanitizer has to anticipate every way
 * markup can be malformed; this one only has to decide, for nodes the parser has
 * already normalised, whether each is on the list.
 */

/**
 * Elements allowed through. Anything structural or interactive is absent by
 * design — a product description has no business carrying a form or a frame.
 */
const ALLOWED_ELEMENTS = new Set([
	'A', 'B', 'BLOCKQUOTE', 'BR', 'CODE', 'EM', 'H2', 'H3', 'H4', 'HR', 'I',
	'LI', 'OL', 'P', 'PRE', 'SPAN', 'STRONG', 'U', 'UL'
]);

/**
 * Elements removed **with their contents**.
 *
 * For everything else that is not allowed the element is unwrapped and its text
 * kept, since a stray `<div>` should not cost the reader a paragraph. These are
 * different: the text inside a `<script>` or `<style>` is code, not prose, and
 * unwrapping it would paste it into the page as visible nonsense at best.
 */
const DROPPED_SUBTREES = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT']);

/** Attributes allowed, per element. Every other attribute is stripped. */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
	A: new Set(['href', 'title'])
};

/** URL schemes a link may use. `javascript:` and `data:` are the reason this list exists. */
const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/**
 * Decides whether a link target is safe to keep.
 *
 * Relative URLs are resolved against the current origin only to be parsed; the
 * original string is what gets written back, so a relative link stays relative.
 */
function isSafeHref(value: string): boolean {
	try {
		const url = new URL(value, window.location.origin);
		return ALLOWED_SCHEMES.has(url.protocol);
	} catch {
		/* Unparseable is not safe — there is no reading of it worth keeping. */
		return false;
	}
}

/** Strips every attribute from an element except the ones its tag allows. */
function cleanAttributes(element: Element): void {
	const allowed = ALLOWED_ATTRIBUTES[element.tagName] ?? new Set<string>();

	/* Copy the list first: removing an attribute mutates the live collection. */
	for (const name of Array.from(element.getAttributeNames())) {
		const lowered = name.toLowerCase();
		if (!allowed.has(lowered)) {
			element.removeAttribute(name);
			continue;
		}
		/* The name came from `getAttributeNames`, so the value is never absent —
		   `String` states that without a fallback branch nothing can reach. */
		if (lowered === 'href' && !isSafeHref(String(element.getAttribute(name)))) {
			element.removeAttribute(name);
		}
	}

	/* A link that survived opens away from the app, and `noreferrer` keeps the
	   opener reference away from a page we do not control. */
	if (element.tagName === 'A' && element.hasAttribute('href')) {
		element.setAttribute('target', '_blank');
		element.setAttribute('rel', 'noopener noreferrer');
	}
}

/**
 * Replaces an element with its own children, keeping the text it wrapped.
 *
 * The parent is passed in rather than read back off the element: the caller is
 * walking that parent's children, so it always has one, and looking it up again
 * would introduce an absent case that cannot occur.
 */
function unwrap(parent: Node, element: Element): void {
	while (element.firstChild) {
		parent.insertBefore(element.firstChild, element);
	}
	parent.removeChild(element);
}

/** Recursively cleans one node's children. */
function cleanChildren(node: Node): void {
	/* Snapshot the children: the walk reparents and removes nodes as it goes. */
	for (const child of Array.from(node.childNodes)) {
		if (child.nodeType === Node.TEXT_NODE) continue;

		if (child.nodeType !== Node.ELEMENT_NODE) {
			/* Comments and processing instructions carry nothing worth rendering. */
			node.removeChild(child);
			continue;
		}

		const element = child as Element;
		if (DROPPED_SUBTREES.has(element.tagName)) {
			node.removeChild(element);
			continue;
		}

		cleanChildren(element);

		if (ALLOWED_ELEMENTS.has(element.tagName)) {
			cleanAttributes(element);
		} else {
			unwrap(node, element);
		}
	}
}

/**
 * Returns a sanitized copy of `html`, safe to pass to `{@html …}`.
 *
 * @param html - Untrusted markup, or `null`/`undefined`.
 * @returns Sanitized markup, or an empty string when there was nothing to render.
 */
export function sanitizeHTML(html: string | null | undefined): string {
	if (!html) return '';
	const parsed = new DOMParser().parseFromString(html, 'text/html');
	cleanChildren(parsed.body);
	return parsed.body.innerHTML;
}
